import { MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../../components/FormPage"
import {
  ApplicationRoleDetail,
  ApplicationRoleGet,
} from "../../../../../../models/ApplicationRole"
import { Guid } from "../../../../../../models/CustomType"
import { DeviceRolePermissionGet } from "../../../../../../models/DeviceRolePermission"
import { ControllerRendererProps } from "../../../../../../types/reactHookForm"

const validationSchema = z.object({
  applicationRoleId: z.string().min(1, "Application role is required"),
})

export function DeviceRolePermissionApplicationRoleCreate() {
  const { deviceId, roleId, permissionId } = useParams()
  const [applicationId, setApplicationId] = useState<Guid>("")
  const [applicationRoles, setApplicationRoles] = useState<ApplicationRoleGet[]>([])
  const [isLoadingApplicationRoles, setIsLoadingApplicationRoles] =
    useState<boolean>(true)
  const [
    deviceRolePermissionApplicationRolesIds,
    setDeviceRolePermissionApplicationRolesIds,
  ] = useState<ApplicationRoleDetail[]>([])
  const [
    isLoadingDeviceRolePermissionApplicationRolesIds,
    setIsLoadingDeviceRolePermissionApplicationRolesIds,
  ] = useState<boolean>(true)

  const navigate = useNavigate()
  const returnURL = `/devices/${deviceId}/roles/${roleId}/permissions/${permissionId}#app-roles`

  useEffect(() => {
    if (applicationId) {
      digitalManagerApi
        .get<ApplicationRoleGet[]>(`/applications/${applicationId}/roles`)
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            setApplicationRoles(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot retrieve application roles")
        })
        .finally(() => {
          setIsLoadingApplicationRoles(false)
        })
    } else {
      setIsLoadingApplicationRoles(false)
    }
  }, [applicationId])

  useEffect(() => {
    if (deviceId && roleId && permissionId) {
      digitalManagerApi
        .get<DeviceRolePermissionGet>(
          `/devices/${deviceId}/roles/${roleId}/permissions/${permissionId}`
        )
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data) {
            setApplicationId(res.data.application.id)
          }
          if (res.data.applicationRoles.length) {
            setDeviceRolePermissionApplicationRolesIds(res.data.applicationRoles)
          }
        })
        .catch((err) => {
          if (err.response.status) {
            navigate(returnURL)
          }
        })
        .finally(() => {
          setIsLoadingDeviceRolePermissionApplicationRolesIds(false)
        })
    }
  }, [deviceId, navigate, permissionId, returnURL, roleId])

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<{ applicationRoleId: string }>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({
      applicationRoleId: "",
    })
  }, [reset])

  const onSubmit = (values: { applicationRoleId: string }) => digitalManagerApi
      .post(
        `/devices/${deviceId}/roles/${roleId}/permissions/${permissionId}/applicationRoles/${values.applicationRoleId}`
      )
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Application's role added to device role's permission")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot add application's role to device role's permission")
      })

  const handleApplicationRoleSelectChange = useCallback(
    (field: ControllerRenderProps<{ applicationRoleId: string }, "applicationRoleId">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderApplicationRoleIdSelect = useCallback(
    ({
      field,
    }: ControllerRendererProps<{ applicationRoleId: string }, "applicationRoleId">) => (
      <MUISelect
        name={field.name}
        id="applicationRoleId"
        size={32}
        dropdownLOV={applicationRoles.map((r) => ({
          id: String(r.id),
          name: r.name ?? "",
          isDisabled: deviceRolePermissionApplicationRolesIds.some((x) => x.id === r.id),
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleApplicationRoleSelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={
          isLoadingApplicationRoles || isLoadingDeviceRolePermissionApplicationRolesIds
        }
      />
    ),
    [
      applicationRoles,
      handleApplicationRoleSelectChange,
      isLoadingApplicationRoles,
      isLoadingDeviceRolePermissionApplicationRolesIds,
      deviceRolePermissionApplicationRolesIds,
    ]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Application role">
        <Controller
          name="applicationRoleId"
          control={control}
          render={renderApplicationRoleIdSelect}
        />
      </FieldRow>
    </FormPage>
  )
}
